// src/components/scribe/RecordingControls.jsx
// Audio recording controls with start/pause/stop functionality

import React, { useState, useEffect } from 'react';
import { scribeRecordingService } from '@/services/scribeRecordingService';
import { 
  Mic, 
  MicOff, 
  Pause, 
  Play, 
  Square,
  AlertCircle
} from 'lucide-react';

// Format duration from ms to MM:SS or HH:MM:SS
const formatDuration = (ms) => {
  if (!ms) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const RecordingControls = ({
  isRecording,
  isPaused,
  duration,
  onStart,
  onPause,
  onResume,
  onStop,
  disabled = false,
  buttonText = 'Start Recording'
}) => {
  const [isSupported, setIsSupported] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    // Check if recording is supported
    setIsSupported(scribeRecordingService.constructor.isSupported());
  }, []);

  const handleStart = async () => {
    if (isStarting) return;
    setPermissionError(null);
    setIsStarting(true);
    try {
      await onStart();
    } finally {
      setIsStarting(false);
    }
  };

  if (!isSupported) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">
            Audio recording is not supported in this browser. Please use Chrome, Safari, or Firefox.
          </p>
        </div>
      </div>
    );
  }

  // Not recording state - show start button
  if (!isRecording) {
    return (
      <div className="flex flex-col items-center gap-4">
        {permissionError && (
          <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">{permissionError}</p>
          </div>
        )}
        
        <button
          onClick={handleStart}
          disabled={disabled || isStarting}
          className="w-full flex items-center justify-center gap-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white py-4 px-6 rounded-xl font-medium transition-colors"
        >
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Mic className="w-5 h-5" />
          </div>
          <span>{isStarting ? 'Starting...' : buttonText}</span>
        </button>
        
        <p className="text-sm text-gray-500 text-center">
          Tap to start recording the patient encounter
        </p>
      </div>
    );
  }

  // Recording state
  return (
    <div className="flex flex-col items-center gap-4">
      {/* Recording indicator and duration */}
      <div className="flex items-center gap-3">
        <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-amber-500' : 'bg-red-500 animate-pulse'}`} />
        <span className="text-2xl font-mono font-semibold text-gray-900">
          {formatDuration(duration)}
        </span>
        <span className="text-sm text-gray-500">
          {isPaused ? 'Paused' : 'Recording'}
        </span>
      </div>

      {/* Visual audio indicator (simplified) */}
      <div className="flex items-center justify-center gap-1 h-12">
        {!isPaused && [...Array(7)].map((_, i) => (
          <div
            key={i}
            className="w-1 bg-red-400 rounded-full animate-pulse"
            style={{
              height: `${Math.random() * 32 + 16}px`,
              animationDelay: `${i * 0.1}s`,
              animationDuration: '0.5s'
            }}
          />
        ))}
        {isPaused && [...Array(7)].map((_, i) => (
          <div
            key={i}
            className="w-1 bg-gray-300 rounded-full"
            style={{ height: '8px' }}
          />
        ))}
      </div>

      {/* Control buttons */}
      <div className="flex items-center gap-4">
        {/* Pause/Resume button */}
        <button
          onClick={isPaused ? onResume : onPause}
          disabled={disabled}
          className="w-14 h-14 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 rounded-full flex items-center justify-center transition-colors"
        >
          {isPaused ? (
            <Play className="w-6 h-6 text-gray-700" />
          ) : (
            <Pause className="w-6 h-6 text-gray-700" />
          )}
        </button>

        {/* Stop button */}
        <button
          onClick={onStop}
          disabled={disabled}
          className="w-16 h-16 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 rounded-full flex items-center justify-center transition-colors shadow-lg"
        >
          <Square className="w-6 h-6 text-white fill-white" />
        </button>

        {/* Placeholder for symmetry */}
        <div className="w-14 h-14" />
      </div>

      <p className="text-sm text-gray-500 text-center">
        {isPaused 
          ? 'Recording paused. Tap play to continue or stop to finish.'
          : 'Tap pause to temporarily stop, or stop when finished.'
        }
      </p>
    </div>
  );
};

export default RecordingControls;